import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import { apiGet, apiPatch, apiDownload } from '../api/client';
import { usePermission } from '../hooks/usePermission';

const STATUS_OPCOES = ['', 'agendado', 'confirmado', 'retirado', 'cancelado'];

const TRANSICOES = {
    agendado: ['confirmado', 'cancelado'],
    confirmado: ['retirado', 'cancelado'],
    retirado: [],
    cancelado: [],
};

export default function Retiradas() {
    const isAdmin = usePermission('admin');

    const [retiradas, setRetiradas] = useState([]);
    const [paginacao, setPaginacao] = useState({});
    const [loading, setLoading] = useState(true);
    const [filtros, setFiltros] = useState({ data: '', tipo: '', patio: '' ,status: '', busca: '' });
    const [pagina, setPagina] = useState(1);
    const [alterando, setAlterando] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);

        const params = new URLSearchParams({ pagina, por_pagina: 25 });

        if (filtros.data) params.set('data', filtros.data);
        if (filtros.tipo) params.set('tipo', filtros.tipo);
        if (filtros.patio) params.set('patio', filtros.patio);
        if (filtros.status) params.set('status', filtros.status);
        if (filtros.busca) params.set('busca', filtros.busca);

        const res = await apiGet(`/api/admin/retiradas?${params}`);

        if (res?.success) {
            setRetiradas(res.data.dados);
            setPaginacao(res.data.paginacao);
        }

        setLoading(false);
    }, [filtros, pagina]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const alterarStatus = async (id, novoStatus) => {
        setAlterando(id);

        const res = await apiPatch(`/api/admin/retiradas/${id}`, { status: novoStatus });

        if (res?.success) carregar();

        setAlterando(null);
    };

    const exportar = (fmt) => {
        const params = new URLSearchParams({ tipo: 'retiradas', formato: fmt });

        if (filtros.data) params.set('data_inicio', filtros.data);
        if (filtros.data) params.set('data_fim', filtros.data);
        if (filtros.status) params.set('status', filtros.status);
        if (filtros.patio) params.set('patio', filtros.patio);

        apiDownload(`/api/admin/exportar?${params}`, `retiradas.${fmt}`);
    };

    const nomeResponsavel = (r) => {
        if (r.tipo_retirante === 'terceiro') {
            return r.nome_terceiro || '—';
        }

        return r.nome_arrematante || r.cliente_nome || '—';
    };

    const subtituloResponsavel = (r) => {
        if (r.tipo_retirante === 'terceiro') {
            return 'Terceiro / Procurador';
        }

        return 'Arrematante';
    };

    const emailResponsavel = (r) => {
        if (r.tipo_retirante === 'terceiro') {
            return r.email_arrematante || r.cliente_email || '';
        }

        return r.email_arrematante || r.cliente_email || '';
    };

    const lotesFormatados = (r) => {
        if (Array.isArray(r.lotes) && r.lotes.length > 0) {
            return r.lotes.join(', ');
        }

        return '—';
    };

    return (
        <Layout>
            <div style={topoPagina}>
                <h1 style={h1}>Retiradas</h1>

                <div style={{ display: 'flex', gap: '8px' }}>
                    <BtnExport onClick={() => exportar('xlsx')} label="Exportar para excel" />
                </div>
            </div>

            <div style={filtrosBox}>
                <input
                    type="date"
                    value={filtros.data}
                    onChange={e => {
                        setFiltros(f => ({ ...f, data: e.target.value }));
                        setPagina(1);
                    }}
                    style={inputFiltro}
                />

                <select
                    value={filtros.tipo}
                    onChange={e => {
                        setFiltros(f => ({ ...f, tipo: e.target.value }));
                        setPagina(1);
                    }}
                    style={inputFiltro}
                >
                    <option value="">Todos os tipos</option>
                    <option value="sucata">Sucata</option>
                    <option value="conservado">Conservado</option>
                </select>

                <select
                    value={filtros.patio}
                    onChange={e => {
                        setFiltros(f => ({ ...f, patio: e.target.value }));
                        setPagina(1);
                    }}
                    style={inputFiltro}
                >
                    <option value="">Todos os pátios</option>
                    <option value="Recife">Recife</option>
                    <option value="Caruaru">Caruaru</option>
                    <option value="Serra Talhada">Serra Talhada</option>
                    <option value="Vitória de Santo Antão">Vitória de Santo Antão</option>
                </select>

                <select
                    value={filtros.status}
                    onChange={e => {
                        setFiltros(f => ({ ...f, status: e.target.value }));
                        setPagina(1);
                    }}
                    style={inputFiltro}
                >
                    {STATUS_OPCOES.map(s => (
                        <option key={s} value={s}>
                            {s || 'Todos os status'}
                        </option>
                    ))}
                </select>

                <input
                    type="text"
                    placeholder="Buscar nome ou e-mail..."
                    value={filtros.busca}
                    onChange={e => {
                        setFiltros(f => ({ ...f, busca: e.target.value }));
                        setPagina(1);
                    }}
                    style={{ ...inputFiltro, minWidth: '220px' }}
                />
            </div>

            <div style={tabelaBox}>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={linhaCabecalho}>
                                    {[
                                        'Arrematante / Terceiro',
                                        'Pátio',
                                        'Situação do lote',
                                        'Nº do lote',
                                        'Data',
                                        'Horário',
                                        'Status',
                                        'Ações',
                                    ].map(h => (
                                        <th key={h} style={th}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {retiradas.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} style={tdVazio}>
                                            Nenhuma retirada encontrada.
                                        </td>
                                    </tr>
                                ) : (
                                    retiradas.map(r => (
                                        <tr key={r.id} style={linhaTabela}>
                                            <td style={td}>
                                                <div style={{ fontWeight: '600' }}>
                                                    {nomeResponsavel(r)}
                                                </div>

                                                <div style={{ fontSize: '12px', color: '#7F8C8D' }}>
                                                    {subtituloResponsavel(r)}
                                                </div>

                                                {emailResponsavel(r) && (
                                                    <div style={{ fontSize: '12px', color: '#7F8C8D' }}>
                                                        {emailResponsavel(r)}
                                                    </div>
                                                )}
                                            </td>

                                            <td style={td}>
                                                {r.patio_retirada || '—'}
                                            </td>

                                            <td style={td}>
                                                <span style={{
                                                    background: r.tipo_lote === 'sucata' ? '#FEF9E7' : '#EAF4FB',
                                                    color: r.tipo_lote === 'sucata' ? '#B7770D' : '#1A5276',
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                }}>
                                                    {r.tipo_lote}
                                                </span>
                                            </td>

                                            <td style={td}>{lotesFormatados(r)}</td>

                                            <td style={td}>{r.data_retirada}</td>

                                            <td style={td}>{r.hora_inicio}</td>

                                            <td style={td}>
                                                <StatusBadge status={r.status} />
                                            </td>

                                            <td style={td}>
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    {TRANSICOES[r.status]
                                                        ?.filter(Boolean)
                                                        .map(s => (
                                                            <button
                                                                key={s}
                                                                disabled={alterando === r.id}
                                                                onClick={() => alterarStatus(r.id, s)}
                                                                style={{
                                                                    padding: '4px 10px',
                                                                    fontSize: '12px',
                                                                    borderRadius: '6px',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    background: s === 'cancelado' ? '#FDEDEC' : '#EAF7EA',
                                                                    color: s === 'cancelado' ? '#922B21' : '#1E8449',
                                                                    fontWeight: '600',
                                                                }}
                                                            >
                                                                {s}
                                                            </button>
                                                        ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        {paginacao.paginas > 1 && (
                            <div style={paginacaoBox}>
                                {Array.from({ length: paginacao.paginas }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setPagina(p)}
                                        style={{
                                            width: '34px',
                                            height: '34px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: p === pagina ? '#1B4F72' : '#EAECEE',
                                            color: p === pagina ? '#fff' : '#2C3E50',
                                            fontWeight: p === pagina ? '700' : '400',
                                        }}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div style={{ marginTop: '12px', color: '#7F8C8D', fontSize: '13px' }}>
                {paginacao.total || 0} retirada(s) encontrada(s)
            </div>
        </Layout>
    );
}

function BtnExport({ onClick, label, secundario }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: '600',
                borderRadius: '6px',
                cursor: 'pointer',
                background: secundario ? '#fff' : '#1B4F72',
                color: secundario ? '#1B4F72' : '#fff',
                border: secundario ? '1px solid #1B4F72' : 'none',
            }}
        >
            {label}
        </button>
    );
}

const topoPagina = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
};

const filtrosBox = {
    background: '#fff',
    borderRadius: '10px',
    padding: '16px 20px',
    marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
};

const tabelaBox = {
    background: '#fff',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const linhaCabecalho = {
    background: '#F8F9FA',
    borderBottom: '2px solid #EAECEE',
};

const linhaTabela = {
    borderBottom: '1px solid #F0F0F0',
};

const paginacaoBox = {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px',
    borderTop: '1px solid #EAECEE',
};

const h1 = {
    margin: 0,
    fontSize: '24px',
    color: '#1B2631',
};

const inputFiltro = {
    padding: '8px 12px',
    border: '1px solid #D5D8DC',
    borderRadius: '6px',
    fontSize: '14px',
};

const th = {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    color: '#566573',
    textTransform: 'uppercase',
};

const td = {
    padding: '12px 14px',
    fontSize: '14px',
    color: '#2C3E50',
    verticalAlign: 'top',
};

const tdVazio = {
    textAlign: 'center',
    padding: '40px',
    color: '#95A5A6',
};